import { merge } from 'es-toolkit'
import { useBoolean } from 'minimal-shared/hooks'

import Box from '@mui/material/Box'
import { useTheme } from '@mui/material/styles'
import { useTranslation } from 'react-i18next'

import { NavMobile } from './nav-mobile'
import { NavVertical } from './nav-vertical'
import { layoutClasses } from '../core/classes'
import { MainSection } from '../core/main-section'
import { MenuButton } from '../components/menu-button'
import { HeaderSection } from '../core/header-section'
import { LayoutSection } from '../core/layout-section'
import { AccountDrawer } from '../components/account-drawer'
// import { LanguagePopover } from '../components/language-popover'
import {
  buildAccountMenu,
  buildHomeMenu,
  buildNavData
} from '../nav-config-dashboard'
import { dashboardLayoutVars, dashboardNavColorVars } from './css-vars'
import { defaultSettings } from '../../components/settings/settings-config'
import ThemeSwitchButton from '../components/theme-switch-button'
import { TransactionHistoryButton } from '../components/transaction-history-button'
import BalanceWithAnimation from '../../components/shared/overview/BalanceWithAnimation'
import { extractInitials } from '../../util/wallet/common.helpers'
import { DashboardContent } from './content'
import PoweredByWrapper from 'wrappers/poweredBy/PoweredByWrapper'

export function DashboardLayout({
  sx,
  cssVars,
  children,
  slotProps,
  layoutQuery = 'lg',
  tokenProfile: { symbol },
  signOut,
  isQuestsEnabled,
  mainProfile: {
    isNavMini,
    brandAppConfig,
    previousUserPoints,
    currentUserPoints
  },
  onToggleNav,
  accountProfile: { firstName, lastName, email },
  onWalletClick,
  connectedWallet,
  onStakingClick,
  onRewardBoxesClick
}) {
  const theme = useTheme()

  const { t } = useTranslation()

  const navVars = dashboardNavColorVars(theme, defaultSettings.navColor)

  const { value: open, onFalse: onClose, onTrue: onOpen } = useBoolean()

  const userInitials = extractInitials(firstName, lastName)

  const navData =
    slotProps?.nav?.data ??
    buildNavData({
      t,
      symbol,
      isQuestsEnabled,
      cashbackEarnOption: brandAppConfig.cashbackEarnOption,
      onWalletClick,
      connectedWallet,
      onStakingClick,
      onRewardBoxesClick
    })

  const isNavVertical = isNavMini || true

  const renderHeader = () => {
    const headerSlotProps = {
      container: {
        maxWidth: false,
        sx: {
          ...(isNavVertical && { px: { [layoutQuery]: 5 } })
        }
      }
    }

    const headerSlots = {
      topArea: null,
      bottomArea: null,
      leftArea: (
        <>
          <MenuButton
            onClick={onOpen}
            sx={{
              mr: 1,
              ml: -1,
              [theme.breakpoints.up(layoutQuery)]: { display: 'none' }
            }}
          />
          <NavMobile
            data={navData}
            open={open}
            onClose={onClose}
            cssVars={navVars.section}
            slots={{ bottomArea: <PoweredByWrapper /> }}
          />
          <BalanceWithAnimation
            t={t}
            symbol={symbol}
            previousUserPoints={previousUserPoints}
            currentUserPoints={currentUserPoints}
          />
        </>
      ),
      rightArea: (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: { xs: 0, sm: 0.75 }
          }}
        >
          {/* <LanguagePopover data={[]} /> */}
          <ThemeSwitchButton />
          <TransactionHistoryButton />
          <AccountDrawer
            data={[buildHomeMenu(t), ...buildAccountMenu({ t })]}
            userInitials={userInitials}
            firstName={firstName}
            lastName={lastName}
            email={email}
            t={t}
            signOut={signOut}
          />
        </Box>
      )
    }

    return (
      <HeaderSection
        layoutQuery={layoutQuery}
        disableElevation={isNavVertical}
        {...slotProps?.header}
        slots={{ ...headerSlots, ...slotProps?.header?.slots }}
        slotProps={merge(headerSlotProps, slotProps?.header?.slotProps ?? {})}
        sx={slotProps?.header?.sx}
      />
    )
  }

  const renderSidebar = () => (
    <NavVertical
      data={navData}
      isNavMini={isNavMini}
      layoutQuery={layoutQuery}
      cssVars={navVars.section}
      onToggleNav={onToggleNav}
      slots={{ bottomArea: <PoweredByWrapper /> }}
    />
  )

  const renderFooter = () => null

  const renderMain = () => (
    <MainSection {...slotProps?.main}>
      <DashboardContent maxWidth='xl'>{children}</DashboardContent>
    </MainSection>
  )

  return (
    <LayoutSection
      /** **************************************
       * @Header
       *************************************** */
      headerSection={renderHeader()}
      /** **************************************
       * @Sidebar
       *************************************** */
      sidebarSection={renderSidebar()}
      /** **************************************
       * @Footer
       *************************************** */
      footerSection={renderFooter()}
      /** **************************************
       * @Styles
       *************************************** */
      cssVars={{ ...dashboardLayoutVars(theme), ...navVars.layout, ...cssVars }}
      sx={[
        {
          [`& .${layoutClasses.sidebarContainer}`]: {
            [theme.breakpoints.up(layoutQuery)]: {
              pl: isNavMini
                ? 'var(--layout-nav-mini-width)'
                : 'var(--layout-nav-vertical-width)',
              transition: theme.transitions.create(['padding-left'], {
                easing: 'var(--layout-transition-easing)',
                duration: 'var(--layout-transition-duration)'
              })
            }
          }
        },
        ...(Array.isArray(sx) ? sx : [sx])
      ]}
    >
      {renderMain()}
    </LayoutSection>
  )
}
